// Login Page

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LoginForm from '../components/auth/LoginForm.jsx';
import ExpensesLoginForm from '../components/auth/ExpensesLoginForm.jsx';
import CarsLoginForm from '../components/auth/CarsLoginForm.jsx';
import { Alert } from '../components/common/index.js';
import { capcrmlogo } from '../assets';
import StorageUtil from '../utils/storage';

const LoginPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);

  const [tab, setTab] = useState(() => {
    const requested = params.get('tab');
    return ['expenses', 'cars'].includes(requested) ? requested : 'admin';
  });
  const [showSessionExpired, setShowSessionExpired] = useState(
    () => ['session_expired', 'inactive'].includes(params.get('reason'))
  );
  // Captured once from the initial URL so it survives the query-strip navigate below.
  const [logoutMessage] = useState(() =>
    params.get('reason') === 'inactive'
      ? 'You were logged out after 30 minutes of inactivity. Please sign in again.'
      : 'Your session has expired. Please sign in again.'
  );

  useEffect(() => {
    if (!showSessionExpired) return;
    if (tab === 'admin') StorageUtil.clearAuth();
    else if (tab === 'cars') StorageUtil.clearCarsAuth();
    else StorageUtil.clearExpensesAuth();
    navigate('/login', { replace: true });
  }, [navigate, showSessionExpired, tab]);

  return (
    <div className="min-h-screen flex">

      {/* Mobile / tablet gradient background (hidden on desktop) */}
      <div className="lg:hidden fixed inset-0 bg-gradient-to-br from-violet-700 via-indigo-800 to-slate-900 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-white/5" />
        <div className="absolute top-1/2 -translate-y-1/2 -left-20 w-64 h-64 rounded-full bg-white/5" />
        <div className="absolute -bottom-16 right-0 w-72 h-72 rounded-full bg-violet-500/20" />
        <div className="absolute bottom-1/3 left-1/2 w-40 h-40 rounded-full bg-indigo-400/10" />
      </div>

      {/* ── Desktop: Left branding panel ── */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 flex-col justify-between p-12 bg-gradient-to-br from-violet-700 via-indigo-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
          <div className="absolute bottom-0 -left-20 w-80 h-80 rounded-full bg-white/5" />
        </div>

        {/* Logo */}
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center">
              <img src={capcrmlogo} alt="CAP" className="w-full h-full object-contain" />
            </div>
            <div>
              <p className="text-white font-bold text-base leading-tight">CAP Admin</p>
              <p className="text-violet-300 text-xs">Coimbatore Auto Parts</p>
            </div>
          </div>
        </div>

        {/* Middle content */}
        <div className="relative space-y-6">
          <div className="w-12 h-1 bg-gradient-to-r from-violet-400 to-transparent rounded-full" />
          <h2 className="text-4xl xl:text-5xl font-black text-white leading-tight">
            Manage your<br />
            <span className="text-violet-300">auto parts</span><br />
            business.
          </h2>
          <p className="text-slate-300 text-base max-w-sm">
            One powerful admin panel to manage products, orders, and customers — all in one place.
          </p>
          <div className="flex flex-wrap gap-2 pt-2">
            {['Products', 'Orders', 'Customers', 'Analytics'].map((tag) => (
              <span key={tag} className="text-xs bg-white/10 text-violet-200 border border-white/10 rounded-full px-3 py-1">{tag}</span>
            ))}
          </div>
        </div>

        <p className="text-slate-400 text-xs relative">© {new Date().getFullYear()} Coimbatore Auto Parts. All rights reserved.</p>
      </div>

      {/* ── Right / Mobile Form Panel ── */}
      <div className="flex-1 flex flex-col items-center justify-start sm:justify-center overflow-y-auto py-10 px-5 sm:p-8 lg:p-6 relative z-10 lg:bg-white lg:justify-center">

        {/* Mobile: Brand header above card */}
        <div className="lg:hidden text-center mb-7 animate-fade-in-up">
          <div className="inline-flex w-16 h-16 rounded-2xl items-center justify-center mb-4">
            <img src={capcrmlogo} alt="CAP" className="w-full h-full object-contain" />
          </div>
          <h2 className="text-white font-black text-2xl tracking-tight">CAP Admin</h2>
          <p className="text-violet-300 text-sm mt-1">Coimbatore Auto Parts</p>
        </div>

        {/* Form card */}
        <div className="w-full max-w-sm animate-fade-in-up" style={{ animationDelay: '0.08s' }}>

          {/* Card shell — styled on mobile/tablet, transparent on desktop */}
          <div className="bg-white rounded-3xl p-7 sm:p-8 shadow-2xl shadow-black/30 lg:shadow-none lg:rounded-none lg:p-0 lg:bg-transparent">

            {/* Desktop: logo inside panel */}
            <div className="hidden lg:flex items-center gap-3 mb-10">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center">
                <img src={capcrmlogo} alt="CAP" className="w-full h-full object-contain" />
              </div>
              <div>
                <p className="font-bold text-slate-800">CAP Admin</p>
                <p className="text-slate-400 text-xs">Coimbatore Auto Parts</p>
              </div>
            </div>

            <h1 className="text-2xl font-bold text-slate-900 mb-1">Welcome back</h1>
            <p className="text-slate-500 text-sm mb-6">Sign in to continue.</p>

            {/* Tab switcher — one per login that exists in the business */}
            <div className="flex gap-1 p-1 rounded-xl bg-slate-100 mb-7">
              {[
                { key: 'admin', label: 'Superadmin' },
                { key: 'expenses', label: 'Admin' },
                { key: 'cars', label: 'Cars' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setTab(key); setShowSessionExpired(false); }}
                  className={`flex-1 py-2 px-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    tab === key
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {showSessionExpired && (
              <Alert
                type="warning"
                message={logoutMessage}
                onClose={() => setShowSessionExpired(false)}
                className="mb-6"
              />
            )}

            {tab === 'admin' && <LoginForm />}
            {tab === 'expenses' && <ExpensesLoginForm />}
            {tab === 'cars' && <CarsLoginForm />}
          </div>
        </div>

        {/* Copyright */}
        <p className="text-xs text-center mt-6 lg:mt-8 text-white/40 lg:text-slate-400 animate-fade-in-up" style={{ animationDelay: '0.16s' }}>
          © {new Date().getFullYear()} Coimbatore Auto Parts · All rights reserved
        </p>
      </div>

    </div>
  );
};

export default LoginPage;
